import React, { useState, useEffect, useRef } from 'react';
import { Cropper } from 'react-cropper';
import { css, StyleSheet } from 'aphrodite/no-important';
import {
  TabsContainer,
  TabItem,
  FileUploader,
  Loader,
  Button,
  Drawer
} from 'quantux-ui';
import WarningDialog from '../warning-dialog';
import CaseScheduleContext from './case-schedule-context';
import InsuranceInfo from '../case-dashboard/insurance-info';
import InsuranceDrawer from '../case-dashboard/insurance-drawer';
import AddPayer from './add-payer';
import Selfpay from './selfpay';

import { blue, gray, salmon } from '../../theme';
import { BASE_URL, FLAG_VALUE, findFlag } from '../../utils';

const addPatientMsg = (
  <div className='p-2'>
    Patient profile must be created to add payers. Go to &quot;Patient&quot; tab to create patient profile.
  </div>
);

const localPlaceholder = (
  <div
    style={{
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
  >
    <h1>Placeholder</h1>
  </div>
);

const COMMON_STYLES = {
  height: '40px',
  borderBottom: 'none',
  marginRight: '0'
};

const TABS_TEXT_STYLES = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: '12px'
};

const BELL_ICON = (
  <svg
    width="16"
    height="16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14 7.439v4.376l2 1.75v.875H9.988C9.878 15.314 9.026 16 8 16c-1.036 0-1.88-.686-1.988-1.56H0v-.875l2-1.75V7.439c0-2.696 1.63-4.936 4.5-5.531v-.595C6.5.586 7.17 0 8 0s1.5.586 1.5 1.313v.595c2.86.595 4.5 2.844 4.5 5.53Z"
      fill="#FF6060"
    />
  </svg>
);

const SUCCESS_ICON = (
  <svg
    width="12"
    height="12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 6a6 6 0 1 1 12 0A6 6 0 1 1 0 6Zm2.646-.238 1.896 1.796L9.36 3l.641.608-5.458 5.17L2 6.369l.646-.607Z"
      fill="#00B080"
    />
  </svg>
);

const PayerTab = (props) => {
  const [patientData, setPatientData] = useState({});
  const [payerActiveTab, setPayerActiveTab] = useState('primary');
  // --- Flags
  const [piFlag, setPiFlag] = useState(FLAG_VALUE.warning);
  const [siFlag, setSiFlag] = useState(FLAG_VALUE.warning);
  const [tiFlag, setTiFlag] = useState(FLAG_VALUE.warning);
  const [spFlag, setSpFlag] = useState(FLAG_VALUE.warning);
  // --- Delete warnings
  const [showPrimaryPayerDeleteWarning, setShowPrimaryPayerDeleteWarning] = useState(false);
  const [showSecondaryPayerDeleteWarning, setShowSecondaryPayerDeleteWarning] = useState(false);
  const [showTertiaryPayerDeleteWarning, setShowTertiaryPayerDeleteWarning] = useState(false);
  // --- Insurance Drawers
  const [showPrimaryInsuranceDrawer, setShowPrimaryInsuranceDrawer] = useState(false);
  const [showSecondaryInsuranceDrawer, setShowSecondaryInsuranceDrawer] = useState(false);
  const [showTertiaryInsuranceDrawer, setShowTertiaryInsuranceDrawer] = useState(false);
  // --- Insurance IDs
  const [showPrimaryInsuranceIdFrontDrawer, setShowPrimaryInsuranceIdFrontDrawer] = useState(false);
  const [piFrontImageUrl, setPiFrontImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/default_insurance.jpg');
  const [showPiFrontCropper, setShowPiFrontCropper] = useState(false);
  const [isPiFrontUploading, setIsPiFrontUploading] = useState(false);
  const [showPiFrontLoader, setShowPiFrontLoader] = useState(false);

  const [showPrimaryInsuranceIdBackDrawer, setShowPrimaryInsuranceIdBackDrawer] = useState(false);
  const [piBackImageUrl, setPiBackImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/default_insurance.jpg');
  const [showPiBackCropper, setShowPiBackCropper] = useState(false);
  const [isPiBackUploading, setIsPiBackUploading] = useState(false);
  const [showPiBackLoader, setShowPiBackLoader] = useState(false);

  const [showSecondaryInsuranceIdFrontDrawer, setShowSecondaryInsuranceIdFrontDrawer] = useState(false);
  const [siFrontImageUrl, setSiFrontImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/default_insurance.jpg');
  const [showSiFrontCropper, setShowSiFrontCropper] = useState(false);
  const [isSiFrontUploading, setIsSiFrontUploading] = useState(false);
  const [showSiFrontLoader, setShowSiFrontLoader] = useState(false);

  const [showSecondaryInsuranceIdBackDrawer, setShowSecondaryInsuranceIdBackDrawer] = useState(false);
  const [siBackImageUrl, setSiBackImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/default_insurance.jpg');
  const [showSiBackCropper, setShowSiBackCropper] = useState(false);
  const [isSiBackUploading, setIsSiBackUploading] = useState(false);
  const [showSiBackLoader, setShowSiBackLoader] = useState(false);

  const [showTertiaryInsuranceIdFrontDrawer, setShowTertiaryInsuranceIdFrontDrawer] = useState(false);
  const [tiFrontImageUrl, setTiFrontImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/default_insurance.jpg');
  const [showTiFrontCropper, setShowTiFrontCropper] = useState(false);
  const [isTiFrontUploading, setIsTiFrontUploading] = useState(false);
  const [showTiFrontLoader, setShowTiFrontLoader] = useState(false);

  const [showTertiaryInsuranceIdBackDrawer, setShowTertiaryInsuranceIdBackDrawer] = useState(false);
  const [tiBackImageUrl, setTiBackImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/default_insurance.jpg');
  const [showTiBackCropper, setShowTiBackCropper] = useState(false);
  const [isTiBackUploading, setIsTiBackUploading] = useState(false);
  const [showTiBackLoader, setShowTiBackLoader] = useState(false);

  /* ___ REFS ________________________________________________________ */
  const piFrontCropperRef = useRef();
  const piFrontFileInputRef = useRef();
  const piBackCropperRef = useRef();
  const piBackFileInputRef = useRef();
  const siFrontCropperRef = useRef();
  const siFrontFileInputRef = useRef();
  const siBackCropperRef = useRef();
  const siBackFileInputRef = useRef();
  const tiFrontCropperRef = useRef();
  const tiFrontFileInputRef = useRef();
  const tiBackCropperRef = useRef();
  const tiBackFileInputRef = useRef();

  /* ___ EFFECTS _____________________________________________________ */
  useEffect(() => {
    setPatientData(props.patientData);

    // Primary Insurance Flag
    if (props.patientData && props.patientData.isNoPrimaryInsurance) {
      setPiFlag(FLAG_VALUE.success);
    } else {
      if (props.patientData && props.patientData.primaryInsurance && Object.keys(props.patientData.primaryInsurance).length > 0) {
        setPiFlag(FLAG_VALUE.success);
      } else {
        setPiFlag(FLAG_VALUE.warning);
      }
    }

    // Secondary Insurance Flag
    if (props.patientData && props.patientData.isNoSecondaryInsurance) {
      setSiFlag(FLAG_VALUE.success);
    } else {
      if (props.patientData && props.patientData.secondaryInsurance && Object.keys(props.patientData.secondaryInsurance).length > 0) {
        setSiFlag(FLAG_VALUE.success);
      } else {
        setSiFlag(FLAG_VALUE.warning);
      }
    }

    // Tertiary Insurance Flag
    if (props.patientData && props.patientData.isNoTertiaryInsurance) {
      setTiFlag(FLAG_VALUE.success);
    } else {
      if (props.patientData && props.patientData.tertiaryInsurance && Object.keys(props.patientData.tertiaryInsurance).length > 0) {
        setTiFlag(FLAG_VALUE.success);
      } else {
        setTiFlag(FLAG_VALUE.warning);
      }
    }

    // Self Pay Flag
    if (props.patientData && props.patientData.isNoSelfPay) {
      setSpFlag(FLAG_VALUE.success);
    } else {
      setSpFlag(FLAG_VALUE.warning);
    }
  }, [props.patientData]);

  /* ___ EVENT HANDLERS ______________________________________________ */
  const handleOnChangeIsNonePayer = (val, payerType) => {
    const formData = new FormData();
    formData.append('patientId', patientData._id);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    let route = '';
    switch (payerType) {
      case 'secondary':
        formData.append('isNoSecondaryInsurance', val);
        route = 'setNoSecondaryInsurance';
        break;
      case 'tertiary':
        formData.append('isNoTertiaryInsurance', val);
        route = 'setNoTertiaryInsurance';
        break;
      case 'primary':
      default:
        formData.append('isNoPrimaryInsurance', val);
        route = 'setNoPrimaryInsurance';
    }

    fetch(`${BASE_URL}/patients/${route}`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
        if (res.success) {
          props.onReloadPatientData();
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnChangeIsNoneSelfPay = (val) => {
    const formData = new FormData();
    formData.append('patientId', patientData._id);
    formData.append('isNone', val);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    fetch(`${BASE_URL}/patients/setNoSelfpay`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
        if (res.success) {
          // getCaseScheduleData();
          console.log('Reload data');
          props.onReloadPatientData();
        }
      })
      .catch(error => console.log('error', error));
  };

  // #region ** Primary Insurance (FRONT) ID Scanner/Cropper Event Handlers
  const uploadPiFront = () => {
    const formData = new FormData();
    const img = piFrontFileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        console.log('Uploading avatar file for cropper');
        console.log(response);
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        console.log('Avatar file for cropper uploaded!');
        console.log(res);
        if (res.success) {
          setPiFrontImageUrl(res.fileUrl);
          setIsPiFrontUploading(false);
          setShowPiFrontCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnPiFrontFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsPiFrontUploading(true);
    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        console.log('Uploading front primary insurance card.');
        console.log(response);
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        console.log('PI Card front for cropper uploaded!');
        console.log(res);
        if (res.success) {
          setPiFrontImageUrl(res.fileUrl);
          setIsPiFrontUploading(false);
          setShowPiFrontCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const uploadCroppedPiFront = (blob) => {
    const headers = new Headers();
    const token = localStorage.getItem('token');
    headers.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('file', blob);
    formData.append('patientId', patientData._id);
    const requestOptions = { method: 'POST', headers, body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/patients/updatePiCardFront`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          console.log('Cropped front image url', res.fileUrl);
          setPiFrontImageUrl(res.fileUrl);
          setShowPiFrontLoader(false);
          props.onReloadPatientData();
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnPiFrontFileSelected = () => {
    if (piFrontFileInputRef.current.files.length > 0) {
      console.log('Selected file = ', piFrontFileInputRef.current.files[0]);
      console.log('typeof img', typeof piFrontFileInputRef.current.files[0]);
      setIsPiFrontUploading(true);
      uploadPiFront();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnClickPiFrontCrop = () => {
    const imageElement = piFrontCropperRef.current;
    const { cropper } = imageElement;
    cropper.getCroppedCanvas().toBlob((blob) => {
      // const url = URL.createObjectURL(blob);
      // piFrontCardRef.current.src = url;
      setShowPiFrontLoader(true);
      uploadCroppedPiFront(blob);
      setShowPrimaryInsuranceIdFrontDrawer(false);
      // closeFrontCropperDrawer();
    });
  };
  // #endregion

  // #region ** Primary Insurance (BACK) ID Scanner/Cropper Event Handlers
  const uploadPiBack = () => {
    const formData = new FormData();
    const img = piBackFileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        console.log('Uploading avatar file for cropper');
        console.log(response);
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        console.log('Avatar file for cropper uploaded!');
        console.log(res);
        if (res.success) {
          setPiBackImageUrl(res.fileUrl);
          setIsPiBackUploading(false);
          setShowPiBackCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnPiBackFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsPiBackUploading(true);
    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        console.log('Uploading back primary insurance card.');
        console.log(response);
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        console.log('PI Card back for cropper uploaded!');
        console.log(res);
        if (res.success) {
          setPiBackImageUrl(res.fileUrl);
          setIsPiBackUploading(false);
          setShowPiBackCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const uploadCroppedPiBack = (blob) => {
    const headers = new Headers();
    const token = localStorage.getItem('token');
    headers.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('file', blob);
    formData.append('patientId', patientData._id);
    const requestOptions = { method: 'POST', headers, body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/patients/updatePiCardBack`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          console.log('Cropped front image url', res.fileUrl);
          setPiBackImageUrl(res.fileUrl);
          setShowPiBackLoader(false);
          props.onReloadPatientData();
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnPiBackFileSelected = () => {
    if (piBackFileInputRef.current.files.length > 0) {
      console.log('Selected file = ', piBackFileInputRef.current.files[0]);
      console.log('typeof img', typeof piBackFileInputRef.current.files[0]);
      setIsPiBackUploading(true);
      uploadPiBack();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnClickPiBackCrop = () => {
    const imageElement = piBackCropperRef.current;
    const { cropper } = imageElement;
    cropper.getCroppedCanvas().toBlob((blob) => {
      // const url = URL.createObjectURL(blob);
      // piBackCardRef.current.src = url;
      setShowPiBackLoader(true);
      uploadCroppedPiBack(blob);
      setShowPrimaryInsuranceIdBackDrawer(false);
    });
  };
  // #endregion

  // #region ** Secondary Insurance (FRONT) ID Scanner/Cropper Event Handlers
  const uploadSiFront = () => {
    const formData = new FormData();
    const img = siFrontFileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        console.log('Uploading avatar file for cropper');
        console.log(response);
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          setSiFrontImageUrl(res.fileUrl);
          setIsSiFrontUploading(false);
          setShowSiFrontCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnSiFrontFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsSiFrontUploading(true);
    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        console.log('SI Card front for cropper uploaded!');
        console.log(res);
        if (res.success) {
          setSiFrontImageUrl(res.fileUrl);
          setIsSiFrontUploading(false);
          setShowSiFrontCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const uploadCroppedSiFront = (blob) => {
    const headers = new Headers();
    const token = localStorage.getItem('token');
    headers.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('file', blob);
    formData.append('patientId', patientData._id);
    const requestOptions = { method: 'POST', headers, body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/patients/updateSiCardFront`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          console.log('Cropped front image url', res.fileUrl);
          setSiFrontImageUrl(res.fileUrl);
          setShowSiFrontLoader(false);
          props.onReloadPatientData();
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnSiFrontFileSelected = () => {
    if (siFrontFileInputRef.current.files.length > 0) {
      console.log('Selected file = ', siFrontFileInputRef.current.files[0]);
      console.log('typeof img', typeof siFrontFileInputRef.current.files[0]);
      setIsSiFrontUploading(true);
      uploadSiFront();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnClickSiFrontCrop = () => {
    const imageElement = siFrontCropperRef.current;
    const { cropper } = imageElement;
    cropper.getCroppedCanvas().toBlob((blob) => {
      // const url = URL.createObjectURL(blob);
      // piFrontCardRef.current.src = url;
      setShowSiFrontLoader(true);
      uploadCroppedSiFront(blob);
      setShowSecondaryInsuranceIdFrontDrawer(false);
      // closeFrontCropperDrawer();
    });
  };
  // #endregion

  // #region ** Secondary Insurance (BACK) ID Scanner/Cropper Event Handlers
  const uploadSiBack = () => {
    const formData = new FormData();
    const img = siBackFileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        console.log('Uploading avatar file for cropper');
        console.log(response);
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          setSiBackImageUrl(res.fileUrl);
          setIsSiBackUploading(false);
          setShowSiBackCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnSiBackFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsSiBackUploading(true);
    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        console.log('SI Card back for cropper uploaded!');
        console.log(res);
        if (res.success) {
          setSiBackImageUrl(res.fileUrl);
          setIsSiBackUploading(false);
          setShowSiBackCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const uploadCroppedSiBack = (blob) => {
    const headers = new Headers();
    const token = localStorage.getItem('token');
    headers.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('file', blob);
    formData.append('patientId', patientData._id);
    const requestOptions = { method: 'POST', headers, body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/patients/updateSiCardBack`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          console.log('Cropped back image url', res.fileUrl);
          setSiBackImageUrl(res.fileUrl);
          setShowSiBackLoader(false);
          props.onReloadPatientData();
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnSiBackFileSelected = () => {
    if (siBackFileInputRef.current.files.length > 0) {
      console.log('Selected file = ', siBackFileInputRef.current.files[0]);
      console.log('typeof img', typeof siBackFileInputRef.current.files[0]);
      setIsSiBackUploading(true);
      uploadSiBack();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnClickSiBackCrop = () => {
    const imageElement = siBackCropperRef.current;
    const { cropper } = imageElement;
    cropper.getCroppedCanvas().toBlob((blob) => {
      // const url = URL.createObjectURL(blob);
      // piBackCardRef.current.src = url;
      setShowSiBackLoader(true);
      uploadCroppedSiBack(blob);
      setShowSecondaryInsuranceIdBackDrawer(false);
      // closeBackCropperDrawer();
    });
  };
  // #endregion

  // #region ** Tertiary Insurance (FRONT) ID Scanner/Cropper Event Handlers
  const uploadTiFront = () => {
    const formData = new FormData();
    const img = tiFrontFileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          setTiFrontImageUrl(res.fileUrl);
          setIsTiFrontUploading(false);
          setShowTiFrontCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnTiFrontFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsTiFrontUploading(true);
    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          setTiFrontImageUrl(res.fileUrl);
          setIsTiFrontUploading(false);
          setShowTiFrontCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const uploadCroppedTiFront = (blob) => {
    const headers = new Headers();
    const token = localStorage.getItem('token');
    headers.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('file', blob);
    formData.append('patientId', patientData._id);
    const requestOptions = { method: 'POST', headers, body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/patients/updateTiCardFront`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          setTiFrontImageUrl(res.fileUrl);
          setShowTiFrontLoader(false);
          props.onReloadPatientData();
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnTiFrontFileSelected = () => {
    if (tiFrontFileInputRef.current.files.length > 0) {
      console.log('Selected file = ', tiFrontFileInputRef.current.files[0]);
      console.log('typeof img', typeof tiFrontFileInputRef.current.files[0]);
      setIsTiFrontUploading(true);
      uploadTiFront();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnClickTiFrontCrop = () => {
    const imageElement = tiFrontCropperRef.current;
    const { cropper } = imageElement;
    cropper.getCroppedCanvas().toBlob((blob) => {
      // const url = URL.createObjectURL(blob);
      // siFrontCardRef.current.src = url;
      setShowTiFrontLoader(true);
      uploadCroppedTiFront(blob);
      setShowTertiaryInsuranceIdFrontDrawer(false);
      // closeFrontCropperDrawer();
    });
  };
  // #endregion

  // #region ** Tertiary Insurance (BACK) ID Scanner/Cropper Event Handlers
  const uploadTiBack = () => {
    const formData = new FormData();
    const img = tiBackFileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          setTiBackImageUrl(res.fileUrl);
          setIsTiBackUploading(false);
          setShowTiBackCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnTiBackFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsTiBackUploading(true);
    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          setTiBackImageUrl(res.fileUrl);
          setIsTiBackUploading(false);
          setShowTiBackCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const uploadCroppedTiBack = (blob) => {
    const headers = new Headers();
    const token = localStorage.getItem('token');
    headers.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('file', blob);
    formData.append('patientId', patientData._id);
    const requestOptions = { method: 'POST', headers, body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/patients/updateTiCardBack`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          setTiBackImageUrl(res.fileUrl);
          setShowTiBackLoader(false);
          props.onReloadPatientData();
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnTiBackFileSelected = () => {
    if (tiBackFileInputRef.current.files.length > 0) {
      console.log('Selected file = ', tiBackFileInputRef.current.files[0]);
      console.log('typeof img', typeof tiBackFileInputRef.current.files[0]);
      setIsTiBackUploading(true);
      uploadTiBack();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnClickTiBackCrop = () => {
    const imageElement = tiBackCropperRef.current;
    const { cropper } = imageElement;
    cropper.getCroppedCanvas().toBlob((blob) => {
      // const url = URL.createObjectURL(blob);
      // siBackCardRef.current.src = url;
      setShowTiBackLoader(true);
      uploadCroppedTiBack(blob);
      setShowTertiaryInsuranceIdBackDrawer(false);
      // closeBackCropperDrawer();
    });
  };
  // #endregion

  // #region ** Delete Insurnace ** Event Handlers
  const deleteInsurance = (type = 'primary') => {
    const formData = new FormData();
    formData.append('patientId', patientData._id);

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    let url = '';
    switch (type) {
      case 'primary':
        url = `${BASE_URL}/patients/deletePrimaryInsurance`;
        break;
      case 'secondary':
        url = `${BASE_URL}/patients/deleteSecondaryInsurance`;
        break;
      case 'tertiary':
        url = `${BASE_URL}/patients/deleteTertiaryInsurance`;
        break;
      default:
        url = '';
    }
    fetch(url, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
        if (res.success) {
          props.onReloadPatientData();
        }
      })
      .catch(error => console.log('error', error));
  };
  // #endregion

  let activePayerTabCompnent = null;
  let activePiComponent = null;
  let activeSiComponent = null;
  let activeTiComponent = null;

  // -- Primary Insurnace Tab
  if (patientData && Object.keys(patientData).length > 0 && patientData.primaryInsurance && Object.keys(patientData.primaryInsurance).length > 0) {
    activePiComponent = (
      <CaseScheduleContext.Provider value={{ readOnlyMode: false }} >
        <InsuranceInfo
          headingText='Primary Insurnace'
          patientData={patientData}
          width={340}
          insuranceType='primary'
          className='p-0'
          onDeletePayer={() => setShowPrimaryPayerDeleteWarning(true)}
          onEditInsuranceInfo={() => {
            setShowPrimaryInsuranceDrawer(true);
          }}
        />
      </CaseScheduleContext.Provider>
    );
  } else {
    activePiComponent = (
      <AddPayer
        message='Primary Payer'
        width={340}
        isNonePayer={patientData && patientData.isNoPrimaryInsurance ? patientData.isNoPrimaryInsurance : false}
        onChangeIsNonePayer={(val) => handleOnChangeIsNonePayer(val, 'primary')}
        onAddPayer={() => setShowPrimaryInsuranceDrawer(true)}
      />
    );
  }

  // -- Secondary Insurnace Tab
  if (patientData && Object.keys(patientData).length > 0 && patientData.secondaryInsurance && Object.keys(patientData.secondaryInsurance).length > 0) {
    activeSiComponent = (
      <CaseScheduleContext.Provider value={{ readOnlyMode: false }} >
        <InsuranceInfo
          headingText='Secondary Insurnace'
          patientData={patientData}
          width={340}
          insuranceType='secondary'
          className='p-0'
          onDeletePayer={() => setShowSecondaryPayerDeleteWarning(true)}
          onEditInsuranceInfo={() => {
            setShowSecondaryInsuranceDrawer(true);
          }}
        />
      </CaseScheduleContext.Provider>
    );
  } else {
    activeSiComponent = (
      <AddPayer
        message='Secondary Payer'
        width={340}
        isNonePayer={patientData && patientData.isNoSecondaryInsurance ? patientData.isNoSecondaryInsurance : false}
        onChangeIsNonePayer={(val) => handleOnChangeIsNonePayer(val, 'secondary')}
        onAddPayer={() => setShowSecondaryInsuranceDrawer(true)}
      />
    );
  }

  // -- Tertiary Insurnace Tab
  if (patientData && Object.keys(patientData).length > 0 && patientData.tertiaryInsurance && Object.keys(patientData.tertiaryInsurance).length > 0) {
    activeTiComponent = (
      <CaseScheduleContext.Provider value={{ readOnlyMode: false }} >
        <InsuranceInfo
          headingText='Tertiary Insurnace'
          patientData={patientData}
          width={340}
          insuranceType='tertiary'
          className='p-0'
          onDeletePayer={() => setShowTertiaryPayerDeleteWarning(true)}
          onEditInsuranceInfo={() => {
            setShowTertiaryInsuranceDrawer(true);
          }}
        />
      </CaseScheduleContext.Provider>
    );
  } else {
    activeTiComponent = (
      <AddPayer
        message='Tertiary Payer'
        width={340}
        isNonePayer={patientData && patientData.isNoTertiaryInsurance ? patientData.isNoTertiaryInsurance : false}
        onChangeIsNonePayer={(val) => handleOnChangeIsNonePayer(val, 'tertiary')}
        onAddPayer={() => setShowTertiaryInsuranceDrawer(true)}
      />
    );
  }

  switch (payerActiveTab) {
    case 'primary':
      activePayerTabCompnent = Object.keys(patientData).length === 0 ? addPatientMsg : activePiComponent;
      break;
    case 'secondary':
      activePayerTabCompnent = Object.keys(patientData).length === 0 ? addPatientMsg : activeSiComponent;
      break;
    case 'tertiary':
      activePayerTabCompnent = Object.keys(patientData).length === 0 ? addPatientMsg : activeTiComponent;
      break;
    case 'selfPay':
      activePayerTabCompnent = Object.keys(patientData).length === 0
        ? addPatientMsg
        : <Selfpay
          isNone={patientData.isNoSelfPay || false}
          onChangeIsNone={(val) => {
            handleOnChangeIsNoneSelfPay(val);
          }}
        />;
      break;
    default:
      activePayerTabCompnent = localPlaceholder;
  }

  /* ___ STYLES ______________________________________________________ */
  const styles = StyleSheet.create({
    sideDrawerContainer: {
      background: '#002448',
      color: gray[300],
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column'
    },
    sideDrawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0'
    },
    sideDrawerHeaderCloseIcon: {
      color: gray[300],
      ':hover': {
        color: salmon[500]
      }
    },
    sideDrawerContentArea: {
      overflowY: 'auto',
      overflowX: 'hidden',
      color: gray[300],
      flexGrow: '1'
    },
    imageUploaderContainer: {},
    imageUploadLoader: {},
    sideDrawerFooter: {
      alignItems: 'center',
      justifyContent: 'flex-end',
      background: '#002448',
      height: '56px',
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      flexShrink: '0',
      paddingRight: '8px',
      position: 'absolute',
      bottom: '0'
    }
  });

  return (
    <>
      {/* Delete Primary Insurnace Warning */}
      {showPrimaryPayerDeleteWarning &&
        <WarningDialog
          show={showPrimaryPayerDeleteWarning}
          title='Delete Primary Insurance'
          message='Do you want to delete patient primary insurance data?'
          onCancel={() => setShowPrimaryPayerDeleteWarning(false)}
          onContinue={() => {
            setShowPrimaryPayerDeleteWarning(false);
            deleteInsurance('primary');
          }}
        />
      }
      {/* Edit Primary Insurnace Data */}
      {showPrimaryInsuranceDrawer &&
        <InsuranceDrawer
          isVisible={showPrimaryInsuranceDrawer}
          insuranceType='primary'
          patientData={patientData}
          payerData={{}}
          headerText='Primary Insurance'
          placement='custom'
          saveUrl='savePatientPrimaryInsuranceData'
          style={{ position: 'absolute', top: '0', right: '0', zIndex: '2300' }}
          onPayerSearch={(value) => { console.log(`Searching payer = ${value}`); }}
          handleOnAddNewPayer={() => { console.log('Adding new payer'); }}
          onShowWarning={() => { console.log('Show payer drawer closing warning!'); }}
          onShowPayerDeleteWarning={() => { console.log('Show payer delete warning'); }}
          onSaveData={() => {
            props.onReloadPatientData();
          }}
          onClose={() => { setShowPrimaryInsuranceDrawer(false); }}
          onInsuranceCardFrontCameraClick={() => {
            setShowPrimaryInsuranceIdFrontDrawer(true);
          }}
          onInsuranceCardBackCameraClick={() => {
            setShowPrimaryInsuranceIdBackDrawer(true);
          }}
        />
      }

      {/* Primary Insurance ID -- Front */}
      {showPrimaryInsuranceIdFrontDrawer &&
        <Drawer
          placement="right"
          visible={showPrimaryInsuranceIdFrontDrawer}
          toggleDrawer={() => setShowPrimaryInsuranceIdFrontDrawer(false)}
          zIndex="2500"
          width={window.innerWidth <= 700 ? window.innerWidth : 375}
        >
          <div className={css(styles.sideDrawerContainer)}>
            {/* Header */}
            <div className={css(styles.sideDrawerHeader)}>
              <div>Upload Primary Insurance Card (Front)</div>
              <i
                className={`fa fa-times-circle ${css(styles.sideDrawerHeaderCloseIcon)}`}
                onClick={() => setShowPrimaryInsuranceIdFrontDrawer(false)}
              />
            </div>
            {/* Content Area */}
            <div className={css(styles.sideDrawerContentArea, styles.imageUploaderContainer)}>
              {showPiFrontCropper
                ? <div>
                  <Cropper
                    src={piFrontImageUrl}
                    style={{ height: 400, width: '100%' }}
                    checkCrossOrigin
                    checkOrientation={false}
                    background={false}
                    viewMode={1}
                    autoCropArea={1}
                    guides={false}
                    ref={piFrontCropperRef}
                  />
                </div>
                : null
              }
              {!showPiFrontCropper
                ? <div>
                  <FileUploader
                    title='Select File'
                    parentRef={piFrontFileInputRef}
                    onDrop={handleOnPiFrontFileDrop}
                    onChange={handleOnPiFrontFileSelected}
                  />
                </div>
                : null
              }
              {isPiFrontUploading
                ? <div className={css(styles.imageUploadLoader)}>
                  <Loader iconType="cog" iconSize="5x" text="Uploading Primary Insurance (Front) Image" />
                </div>
                : null
              }
            </div>
            {/* Drawer footer area */}
            <div
              className={`fx fx__hv-center ${css(styles.sideDrawerFooter)}`}
              style={{ position: 'absolute', bottom: '0' }}
            >
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                onClick={handleOnClickPiFrontCrop}
                style={{
                  height: '32px',
                  width: '340px',
                  padding: '3px 0'
                }}
              >
                Crop Image
              </Button>
            </div>
          </div>
        </Drawer>
      }

      {/* Primary Insurance ID -- Back */}
      {showPrimaryInsuranceIdBackDrawer &&
        <Drawer
          placement="right"
          visible={showPrimaryInsuranceIdBackDrawer}
          toggleDrawer={() => setShowPrimaryInsuranceIdBackDrawer(false)}
          zIndex="2500"
          width={window.innerWidth <= 700 ? window.innerWidth : 375}
        >
          <div className={css(styles.sideDrawerContainer)}>
            {/* Header */}
            <div className={css(styles.sideDrawerHeader)}>
              <div>Upload Primary Insurance Card (Back)</div>
              <i
                className={`fa fa-times-circle ${css(styles.sideDrawerHeaderCloseIcon)}`}
                onClick={() => setShowPrimaryInsuranceIdBackDrawer(false)}
              />
            </div>
            {/* Content Area */}
            <div className={css(styles.sideDrawerContentArea, styles.imageUploaderContainer)}>
              {showPiBackCropper
                ? <div>
                  <Cropper
                    src={piBackImageUrl}
                    style={{ height: 400, width: '100%' }}
                    checkCrossOrigin
                    checkOrientation={false}
                    background={false}
                    viewMode={1}
                    autoCropArea={1}
                    guides={false}
                    ref={piBackCropperRef}
                  />
                </div>
                : null
              }
              {!showPiBackCropper
                ? <div>
                  <FileUploader
                    title='Select File'
                    parentRef={piBackFileInputRef}
                    onDrop={handleOnPiBackFileDrop}
                    onChange={handleOnPiBackFileSelected}
                  />
                </div>
                : null
              }
              {isPiBackUploading
                ? <div className={css(styles.imageUploadLoader)}>
                  <Loader iconType="cog" iconSize="5x" text="Uploading Primary Insurance (Back) Image" />
                </div>
                : null
              }
            </div>
            {/* Drawer footer area */}
            <div
              className={`fx fx__hv-center ${css(styles.sideDrawerFooter)}`}
              style={{ position: 'absolute', bottom: '0' }}
            >
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                onClick={handleOnClickPiBackCrop}
                style={{
                  height: '32px',
                  width: '340px',
                  padding: '3px 0'
                }}
              >
                Crop Image
              </Button>
            </div>
          </div>
        </Drawer>
      }

      {/* Delete Secondary Insurnace Warning */}
      {showSecondaryPayerDeleteWarning &&
        <WarningDialog
          show={showSecondaryPayerDeleteWarning}
          title='Delete Secondary Insurance'
          message='Do you want to delete patient secondary insurance data?'
          onCancel={() => setShowSecondaryPayerDeleteWarning(false)}
          onContinue={() => {
            setShowSecondaryPayerDeleteWarning(false);
            deleteInsurance('secondary');
          }}
        />
      }

      {/* Edit Secondary Insurnace Data */}
      {showSecondaryInsuranceDrawer &&
        <InsuranceDrawer
          isVisible={showSecondaryInsuranceDrawer}
          insuranceType='secondary'
          patientData={patientData}
          payerData={{}}
          headerText='Secondary Insurance'
          placement='custom'
          saveUrl='savePatientSecondaryInsuranceData'
          style={{ position: 'absolute', top: '0', right: '0', zIndex: '2300' }}
          onPayerSearch={(value) => { console.log(`Searching payer = ${value}`); }}
          handleOnAddNewPayer={() => { console.log('Adding new payer'); }}
          onShowWarning={() => { console.log('Show payer drawer closing warning!'); }}
          onShowPayerDeleteWarning={() => { console.log('Show payer delete warning'); }}
          onSaveData={() => {
            props.onReloadPatientData();
          }}
          onClose={() => { setShowSecondaryInsuranceDrawer(false); }}
          onInsuranceCardFrontCameraClick={() => { setShowSecondaryInsuranceIdFrontDrawer(true); }}
          onInsuranceCardBackCameraClick={() => { setShowSecondaryInsuranceIdBackDrawer(true); }}
        />
      }

      {/* Secondary Insurance ID -- Front */}
      {showSecondaryInsuranceIdFrontDrawer &&
        <Drawer
          placement="right"
          visible={showSecondaryInsuranceIdFrontDrawer}
          toggleDrawer={() => setShowSecondaryInsuranceIdFrontDrawer(false)}
          zIndex="2500"
          width={window.innerWidth <= 700 ? window.innerWidth : 375}
        >
          <div className={css(styles.sideDrawerContainer)}>
            {/* Header */}
            <div className={css(styles.sideDrawerHeader)}>
              <div>Upload Secondary Insurance Card (Front)</div>
              <i
                className={`fa fa-times-circle ${css(styles.sideDrawerHeaderCloseIcon)}`}
                onClick={() => setShowSecondaryInsuranceIdFrontDrawer(false)}
              />
            </div>
            {/* Content Area */}
            <div className={css(styles.sideDrawerContentArea, styles.imageUploaderContainer)}>
              {showSiFrontCropper
                ? <div>
                  <Cropper
                    src={siFrontImageUrl}
                    style={{ height: 400, width: '100%' }}
                    checkCrossOrigin
                    checkOrientation={false}
                    background={false}
                    viewMode={1}
                    autoCropArea={1}
                    guides={false}
                    ref={siFrontCropperRef}
                  />
                </div>
                : null
              }
              {!showSiFrontCropper
                ? <div>
                  <FileUploader
                    title='Select File'
                    parentRef={siFrontFileInputRef}
                    onDrop={handleOnSiFrontFileDrop}
                    onChange={handleOnSiFrontFileSelected}
                  />
                </div>
                : null
              }
              {isSiFrontUploading
                ? <div className={css(styles.imageUploadLoader)}>
                  <Loader iconType="cog" iconSize="5x" text="Uploading Secondary Insurance (Front) Image" />
                </div>
                : null
              }
            </div>
            {/* Drawer footer area */}
            <div
              className={`fx fx__hv-center ${css(styles.sideDrawerFooter)}`}
              style={{ position: 'absolute', bottom: '0' }}
            >
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                onClick={handleOnClickSiFrontCrop}
                style={{
                  height: '32px',
                  width: '340px',
                  padding: '3px 0'
                }}
              >
                Crop Image
              </Button>
            </div>
          </div>
        </Drawer>
      }

      {/* Secondary Insurance ID -- Back */}
      {showSecondaryInsuranceIdBackDrawer &&
        <Drawer
          placement="right"
          visible={showSecondaryInsuranceIdBackDrawer}
          toggleDrawer={() => setShowSecondaryInsuranceIdBackDrawer(false)}
          zIndex="2500"
          width={window.innerWidth <= 700 ? window.innerWidth : 375}
        >
          <div className={css(styles.sideDrawerContainer)}>
            {/* Header */}
            <div className={css(styles.sideDrawerHeader)}>
              <div>Upload Secondary Insurance Card (Back)</div>
              <i
                className={`fa fa-times-circle ${css(styles.sideDrawerHeaderCloseIcon)}`}
                onClick={() => setShowSecondaryInsuranceIdBackDrawer(false)}
              />
            </div>
            {/* Content Area */}
            <div className={css(styles.sideDrawerContentArea, styles.imageUploaderContainer)}>
              {showSiBackCropper
                ? <div>
                  <Cropper
                    src={siBackImageUrl}
                    style={{ height: 400, width: '100%' }}
                    checkCrossOrigin
                    checkOrientation={false}
                    background={false}
                    viewMode={1}
                    autoCropArea={1}
                    guides={false}
                    ref={siBackCropperRef}
                  />
                </div>
                : null
              }
              {!showSiBackCropper
                ? <div>
                  <FileUploader
                    title='Select File'
                    parentRef={siBackFileInputRef}
                    onDrop={handleOnSiBackFileDrop}
                    onChange={handleOnSiBackFileSelected}
                  />
                </div>
                : null
              }
              {isSiBackUploading
                ? <div className={css(styles.imageUploadLoader)}>
                  <Loader iconType="cog" iconSize="5x" text="Uploading Secondary Insurance (Back) Image" />
                </div>
                : null
              }
            </div>
            {/* Drawer footer area */}
            <div
              className={`fx fx__hv-center ${css(styles.sideDrawerFooter)}`}
              style={{ position: 'absolute', bottom: '0' }}
            >
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                onClick={handleOnClickSiBackCrop}
                style={{
                  height: '32px',
                  width: '340px',
                  padding: '3px 0'
                }}
              >
                Crop Image
              </Button>
            </div>
          </div>
        </Drawer>
      }

      {/* Delete Tertiary Insurnace Warning */}
      {showTertiaryPayerDeleteWarning &&
        <WarningDialog
          show={showTertiaryPayerDeleteWarning}
          title='Delete Tertiary Insurance'
          message='Do you want to delete patient tertiary insurance data?'
          onCancel={() => setShowTertiaryPayerDeleteWarning(false)}
          onContinue={() => {
            setShowTertiaryPayerDeleteWarning(false);
            deleteInsurance('tertiary');
          }}
        />
      }

      {/* Edit Tertiary Insurnace Data */}
      {showTertiaryInsuranceDrawer &&
        <InsuranceDrawer
          isVisible={showTertiaryInsuranceDrawer}
          insuranceType='tertiary'
          patientData={patientData}
          payerData={{}}
          headerText='Tertiary Insurance'
          placement='custom'
          saveUrl='savePatientTertiaryInsuranceData'
          style={{ position: 'absolute', top: '0', right: '0', zIndex: '2300' }}
          onPayerSearch={(value) => { console.log(`Searching payer = ${value}`); }}
          handleOnAddNewPayer={() => { console.log('Adding new payer'); }}
          onShowWarning={() => { console.log('Show payer drawer closing warning!'); }}
          onShowPayerDeleteWarning={() => { console.log('Show payer delete warning'); }}
          onSaveData={() => {
            props.onReloadPatientData();
          }}
          onClose={() => { setShowTertiaryInsuranceDrawer(false); }}
          onInsuranceCardFrontCameraClick={() => { setShowTertiaryInsuranceIdFrontDrawer(true); }}
          onInsuranceCardBackCameraClick={() => { setShowTertiaryInsuranceIdBackDrawer(true); }}
        />
      }

      {/* Tertiary Insurance ID -- Front */}
      {showTertiaryInsuranceIdFrontDrawer &&
        <Drawer
          placement="right"
          visible={showTertiaryInsuranceIdFrontDrawer}
          toggleDrawer={() => setShowTertiaryInsuranceIdFrontDrawer(false)}
          zIndex="2500"
          width={window.innerWidth <= 700 ? window.innerWidth : 375}
        >
          <div className={css(styles.sideDrawerContainer)}>
            {/* Header */}
            <div className={css(styles.sideDrawerHeader)}>
              <div>Upload Tertiary Insurance Card (Front)</div>
              <i
                className={`fa fa-times-circle ${css(styles.sideDrawerHeaderCloseIcon)}`}
                onClick={() => setShowTertiaryInsuranceIdFrontDrawer(false)}
              />
            </div>
            {/* Content Area */}
            <div className={css(styles.sideDrawerContentArea, styles.imageUploaderContainer)}>
              {showTiFrontCropper
                ? <div>
                  <Cropper
                    src={tiFrontImageUrl}
                    style={{ height: 400, width: '100%' }}
                    checkCrossOrigin
                    checkOrientation={false}
                    background={false}
                    viewMode={1}
                    autoCropArea={1}
                    guides={false}
                    ref={tiFrontCropperRef}
                  />
                </div>
                : null
              }
              {!showTiFrontCropper
                ? <div>
                  <FileUploader
                    title='Select File'
                    parentRef={tiFrontFileInputRef}
                    onDrop={handleOnTiFrontFileDrop}
                    onChange={handleOnTiFrontFileSelected}
                  />
                </div>
                : null
              }
              {isTiFrontUploading
                ? <div className={css(styles.imageUploadLoader)}>
                  <Loader iconType="cog" iconSize="5x" text="Uploading Tertiary Insurance (Front) Image" />
                </div>
                : null
              }
            </div>
            {/* Drawer footer area */}
            <div
              className={`fx fx__hv-center ${css(styles.sideDrawerFooter)}`}
              style={{ position: 'absolute', bottom: '0' }}
            >
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                onClick={handleOnClickTiFrontCrop}
                style={{
                  height: '32px',
                  width: '340px',
                  padding: '3px 0'
                }}
              >
                Crop Image
              </Button>
            </div>
          </div>
        </Drawer>
      }

      {/* Tertiary Insurance ID -- Back */}
      {showTertiaryInsuranceIdBackDrawer &&
        <Drawer
          placement="right"
          visible={showTertiaryInsuranceIdBackDrawer}
          toggleDrawer={() => setShowTertiaryInsuranceIdBackDrawer(false)}
          zIndex="2500"
          width={window.innerWidth <= 700 ? window.innerWidth : 375}
        >
          <div className={css(styles.sideDrawerContainer)}>
            {/* Header */}
            <div className={css(styles.sideDrawerHeader)}>
              <div>Upload Tertiary Insurance Card (Back)</div>
              <i
                className={`fa fa-times-circle ${css(styles.sideDrawerHeaderCloseIcon)}`}
                onClick={() => setShowTertiaryInsuranceIdBackDrawer(false)}
              />
            </div>
            {/* Content Area */}
            <div className={css(styles.sideDrawerContentArea, styles.imageUploaderContainer)}>
              {showTiBackCropper
                ? <div>
                  <Cropper
                    src={tiBackImageUrl}
                    style={{ height: 400, width: '100%' }}
                    checkCrossOrigin
                    checkOrientation={false}
                    background={false}
                    viewMode={1}
                    autoCropArea={1}
                    guides={false}
                    ref={tiBackCropperRef}
                  />
                </div>
                : null
              }
              {!showTiBackCropper
                ? <div>
                  <FileUploader
                    title='Select File'
                    parentRef={tiBackFileInputRef}
                    onDrop={handleOnTiBackFileDrop}
                    onChange={handleOnTiBackFileSelected}
                  />
                </div>
                : null
              }
              {isTiBackUploading
                ? <div className={css(styles.imageUploadLoader)}>
                  <Loader iconType="cog" iconSize="5x" text="Uploading Tertiary Insurance (Back) Image" />
                </div>
                : null
              }
            </div>
            {/* Drawer footer area */}
            <div
              className={`fx fx__hv-center ${css(styles.sideDrawerFooter)}`}
              style={{ position: 'absolute', bottom: '0' }}
            >
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                onClick={handleOnClickTiBackCrop}
                style={{
                  height: '32px',
                  width: '340px',
                  padding: '3px 0'
                }}
              >
                Crop Image
              </Button>
            </div>
          </div>
        </Drawer>
      }

      <TabsContainer
        dropDownId="payer_sub_tabs"
        showPlusIcon={false}
        isDrawer
        shiftMethod='minWidth'
        minWidth={610}
        containerStyles={{ width: '100%' }}
      >
        {/* Primary Insurance */}
        <TabItem
          key='1611'
          tabKey='2000'
          parentKey='3000'
          text='Primary'
          isActive={payerActiveTab === 'primary'}
          showTabIcon
          showSvgIcon
          svgIcon={piFlag === FLAG_VALUE.warning ? BELL_ICON : SUCCESS_ICON}
          style={{
            ...COMMON_STYLES,
            width: '25%',
            minWidth: '85px',
            maxWidth: '25%',
            background: '#184878',
            borderBottom: payerActiveTab === 'primary' ? `1px solid ${blue[500]}` : 'none'
          }}
          textStyles={TABS_TEXT_STYLES}
          onActivate={() => {
            setPayerActiveTab('primary');
          }}
        >
          Primary
        </TabItem>
        {/* Secondary Insurance */}
        <TabItem
          key='1612'
          tabKey='2001'
          parentKey='3001'
          text='Secondary'
          isActive={payerActiveTab === 'secondary'}
          showTabIcon
          showSvgIcon
          svgIcon={siFlag === FLAG_VALUE.warning ? BELL_ICON : SUCCESS_ICON}
          style={{
            ...COMMON_STYLES,
            width: '25%',
            minWidth: '100px',
            maxWidth: '25%',
            background: '#184878',
            borderBottom: payerActiveTab === 'secondary' ? `1px solid ${blue[500]}` : 'none'
          }}
          textStyles={TABS_TEXT_STYLES}
          onActivate={() => {
            setPayerActiveTab('secondary');
          }}
        >
          Secondary
        </TabItem>
        {/* Tertiary Insurance */}
        <TabItem
          key='1613'
          tabKey='2002'
          parentKey='3002'
          text='Tertiary'
          isActive={payerActiveTab === 'tertiary'}
          showTabIcon
          showSvgIcon
          svgIcon={tiFlag === FLAG_VALUE.warning ? BELL_ICON : SUCCESS_ICON}
          style={{
            ...COMMON_STYLES,
            width: '25%',
            minWidth: '85px',
            maxWidth: '25%',
            background: '#184878',
            borderBottom: payerActiveTab === 'tertiary' ? `1px solid ${blue[500]}` : 'none'
          }}
          textStyles={TABS_TEXT_STYLES}
          onActivate={() => {
            setPayerActiveTab('tertiary');
          }}
        >
          Tertiary
        </TabItem>
        {/* Self Pay */}
        <TabItem
          key='1614'
          tabKey='2008'
          parentKey='3010'
          text='Self Pay'
          isActive={payerActiveTab === 'selfPay'}
          showTabIcon
          showSvgIcon
          svgIcon={spFlag === FLAG_VALUE.warning ? BELL_ICON : SUCCESS_ICON}
          style={{
            ...COMMON_STYLES,
            width: '25%',
            minWidth: '85px',
            maxWidth: '25%',
            background: '#184878',
            borderBottom: payerActiveTab === 'selfPay' ? `1px solid ${blue[500]}` : 'none'
          }}
          textStyles={TABS_TEXT_STYLES}
          onActivate={() => {
            setPayerActiveTab('selfPay');
          }}
        >
          Self Pay
        </TabItem>
      </TabsContainer>
      <div
        className='p-2'
        style={{ height: '100%', overflow: 'hidden auto' }}
      >
        {activePayerTabCompnent}
      </div>
    </>
  );
};

PayerTab.defaultProps = {
  patientData: {}
};

export default PayerTab;
